import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { api, errMsg, fmtMoney } from "../../lib/api";
import { BOOK_SERVICES, BOOK_SIZES } from "../../lib/constants";
import { fileToDataUrl } from "../../lib/upload";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { AddressInput } from "../../components/shared/AddressInput";
import { RouteMap } from "../../components/shared/RouteMap";
import { WheelSheet, WheelColumn } from "../../components/shared/WheelPicker";
import { ItemDetailsSheet, TimeWheelSheet, fmtTime } from "../../components/shared/ItemDetailsSheet";
import { toast } from "sonner";
import {
  ArrowRight, ArrowLeft, CheckCircle2, Loader2, ShieldCheck, Plus, Minus, X, Zap, Tag,
  CreditCard, Camera, ScanEye, Sparkles, Route, CalendarDays, Upload, MapPin, Clock, SlidersHorizontal,
} from "lucide-react";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const ITEM_CHIPS = {
  moving: ["Couch / Sofa", "Bed frame", "Mattress", "Dresser", "Dining set", "Appliance", "TV", "Desk", "Boxes", "Exercise equipment"],
  white_glove_delivery: ["Couch / Sofa", "Mattress", "Appliance", "TV", "Dining set", "Furniture", "Exercise equipment", "Boxes", "Special delivery"],
  junk_removal: ["Furniture", "Appliance", "Mattress", "Electronics", "Yard debris", "Construction debris", "Boxes / clutter"],
  furniture_assembly: ["Bed frame", "Dresser", "Desk", "Bookshelf", "Dining set", "Office furniture", "Outdoor / patio"],
  labor: [],
};
const STEP_META = {
  service: { overline: "SELECT OPERATION", title: "What's the mission?" },
  size: { overline: "MISSION SCOPE", title: "How big is the job?" },
  details: { overline: "LOAD DETAILS", title: "Tell us what we're handling." },
  route: { overline: "ROUTE", title: "Where are we operating?" },
  schedule: { overline: "DEPLOY WINDOW", title: "When do we roll?" },
  photos: { overline: "VISUAL INTEL", title: "Show us the job." },
  summary: { overline: "MISSION BRIEFING", title: "Review & confirm." },
};
const STEPS = ["service", "size", "details", "route", "schedule", "photos", "summary"];

const prettyDate = (iso) => iso ? new Date(iso + "T12:00:00").toLocaleDateString("en-US", { weekday: "short", month: "long", day: "numeric" }) : "";

function DateSheet({ open, value, onCancel, onDone }) {
  const init = value ? new Date(value + "T12:00:00") : new Date();
  const [m, setM] = useState(init.getMonth());
  const [d, setD] = useState(init.getDate());
  const [y, setY] = useState(init.getFullYear());
  const yearNow = new Date().getFullYear();
  const dim = new Date(y, m + 1, 0).getDate();
  const day = Math.min(d, dim);
  const done = () => {
    let picked = new Date(y, m, day, 12);
    const floor = new Date(); floor.setHours(0, 0, 0, 0);
    if (picked < floor) picked = new Date();
    onDone(`${picked.getFullYear()}-${String(picked.getMonth() + 1).padStart(2, "0")}-${String(picked.getDate()).padStart(2, "0")}`);
  };
  return (
    <WheelSheet open={open} title="Mission Date" onCancel={onCancel} onDone={done}>
      <WheelColumn className="flex-[1.5]" options={MONTHS.map((mm, i) => ({ value: i, label: mm }))} value={m} onChange={setM} />
      <WheelColumn className="flex-1" options={Array.from({ length: dim }, (_, i) => ({ value: i + 1, label: String(i + 1) }))} value={day} onChange={setD} />
      <WheelColumn className="flex-1" options={[yearNow, yearNow + 1].map((yy) => ({ value: yy, label: String(yy) }))} value={y} onChange={setY} />
    </WheelSheet>
  );
}

const chip = (active) =>
  `rounded-2xl border px-5 py-4 text-base font-semibold transition-colors duration-200 text-left min-h-[52px] ${
    active ? "border-[var(--cc-accent)] bg-[var(--cc-accent)]/10 text-[var(--cc-accent)]" : "bg-card hover:border-foreground/25"}`;

const YesNo = ({ value, onChange, testid }) => (
  <div className="grid grid-cols-2 gap-3">
    <button data-testid={`${testid}-yes`} onClick={() => onChange(true)} className={`${chip(value === true)} text-center`}>Yes</button>
    <button data-testid={`${testid}-no`} onClick={() => onChange(false)} className={`${chip(value === false)} text-center`}>No</button>
  </div>
);

export default function Book() {
  const location = useLocation();
  const { user } = useAuth();
  const [stepIdx, setStepIdx] = useState(0);
  const [booked, setBooked] = useState(null);
  const [loading, setLoading] = useState(false);
  const [depositLoading, setDepositLoading] = useState(false);
  const [estimate, setEstimate] = useState(null);
  const [route, setRoute] = useState(null);
  const [routeLoading, setRouteLoading] = useState(false);
  const [promoInput, setPromoInput] = useState("");
  const [promoInfo, setPromoInfo] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [aiEst, setAiEst] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [dateOpen, setDateOpen] = useState(false);
  const [timeOpen, setTimeOpen] = useState(false);
  const [catalog, setCatalog] = useState(null);
  const [detailSheet, setDetailSheet] = useState({ open: false, name: "" });
  // items = [{name, qty, spec, addons}] — coerce guards against stale HMR state
  const [itemsRaw, setItems] = useState([]);
  const items = Array.isArray(itemsRaw) ? itemsRaw : [];
  const globalFileRef = useRef(null);
  const [form, setForm] = useState({
    service_type: location.state?.service || "", size: "few",
    same_place: false, special_items: 0, stairs: false, stairs_flights: 1, electrical: false,
    priority: false, promo_code: "",
    pickup: { label: "", lat: null, lng: null }, dropoff: { label: "", lat: null, lng: null },
    extra_stop_addresses: [], date: "", time: "09:00", details: "",
  });
  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setAiEst(null); };
  const step = STEPS[stepIdx];
  const stopCount = form.extra_stop_addresses.filter((a) => a.trim()).length;

  useEffect(() => {
    api.get("/bookings/catalog").then((r) => setCatalog(r.data)).catch(() => {});
  }, []);

  const estPayload = {
    service_type: form.service_type, size: form.size, same_place: form.same_place,
    special_items: form.special_items, stairs: form.stairs, stairs_flights: form.stairs_flights,
    electrical: form.electrical, distance_miles: form.same_place ? 0 : (route?.distance_miles || 0),
    extra_stops: stopCount, priority: form.priority, promo_code: form.promo_code,
    items,
  };

  useEffect(() => {
    if (stepIdx >= 1 && form.service_type) {
      api.post("/bookings/estimate", estPayload).then((r) => setEstimate(r.data)).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepIdx, form.service_type, form.size, form.same_place, form.special_items, form.stairs, form.stairs_flights,
      form.electrical, route?.distance_miles, stopCount, form.priority, form.promo_code, items]);

  useEffect(() => {
    const a = form.pickup, b = form.dropoff;
    if (form.same_place || a.lat == null || b.lat == null) { setRoute(null); return; }
    setRouteLoading(true);
    api.post("/geo/route", { pickup: a, dropoff: b })
      .then((r) => setRoute(r.data))
      .catch(() => setRoute(null))
      .finally(() => setRouteLoading(false));
  }, [form.pickup.lat, form.pickup.lng, form.dropoff.lat, form.dropoff.lng, form.same_place]); // eslint-disable-line

  useEffect(() => {
    if (step === "summary" && photos.length > 0 && !aiEst && !aiLoading) runAiEstimate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, photos.length]);

  const runAiEstimate = async () => {
    setAiLoading(true);
    try {
      const { data } = await api.post("/bookings/ai-estimate", { ...estPayload, details: form.details, photos });
      setAiEst(data);
    } catch (e) { toast.error(errMsg(e)); }
    setAiLoading(false);
  };

  const addPhotos = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 6 - photos.length);
    if (!files.length) return;
    const converted = await Promise.all(files.map((f) => fileToDataUrl(f, 1280).catch(() => null)));
    const ok = converted.filter(Boolean);
    setPhotos((p) => [...p, ...ok].slice(0, 6));
    setAiEst(null);
    if (ok.length) toast.success(`${ok.length} photo${ok.length !== 1 ? "s" : ""} attached — we'll refine your estimate`);
    e.target.value = "";
  };

  const applyCode = async () => {
    if (!promoInput.trim()) return;
    try {
      const { data } = await api.post("/bookings/validate-code", { code: promoInput });
      if (data.valid) {
        set("promo_code", promoInput.trim().toUpperCase());
        setPromoInfo(data);
        toast.success(`${data.label} — ${data.percent_off}% off applied`);
      } else toast.error("That code isn't valid or has expired");
    } catch { toast.error("Couldn't validate the code — try again"); }
  };

  const confirm = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/bookings", {
        ...estPayload,
        pickup_address: form.pickup.label, dropoff_address: form.same_place ? "" : form.dropoff.label,
        pickup_lat: form.pickup.lat, pickup_lng: form.pickup.lng,
        dropoff_lat: form.dropoff.lat, dropoff_lng: form.dropoff.lng,
        extra_stop_addresses: form.extra_stop_addresses,
        date: form.date, time: form.time, details: form.details,
        ai_estimate_id: aiEst?.estimate_id || "",
      });
      setBooked(data);
    } catch (e) { toast.error(errMsg(e)); }
    setLoading(false);
  };

  const payDeposit = async () => {
    setDepositLoading(true);
    try {
      const { data } = await api.post("/payments/booking-deposit", { mission_id: booked.mission.id, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(errMsg(e)); setDepositLoading(false); }
  };

  const findItem = (name) => items.find((it) => it.name === name);
  const upsertItem = (name, patch) => {
    setItems((prev) => {
      const idx = prev.findIndex((it) => it.name === name);
      if (idx < 0) return [...prev, { name, qty: 1, spec: "", addons: [], ...patch }];
      const next = [...prev]; next[idx] = { ...next[idx], ...patch }; return next;
    });
    setAiEst(null);
  };
  const incItem = (name, delta) => {
    setItems((prev) => {
      const idx = prev.findIndex((it) => it.name === name);
      if (idx < 0 && delta > 0) return [...prev, { name, qty: 1, spec: "", addons: [] }];
      if (idx < 0) return prev;
      const q = prev[idx].qty + delta;
      if (q <= 0) return prev.filter((_, j) => j !== idx);
      const next = [...prev]; next[idx] = { ...next[idx], qty: q }; return next;
    });
    setAiEst(null);
  };
  const removeItem = (name) => { setItems((p) => p.filter((it) => it.name !== name)); setAiEst(null); };

  if (booked) {
    const dep = booked.estimate.deposit_amount;
    return (
      <div className="max-w-xl mx-auto px-5 py-20 text-center fade-up">
        <CheckCircle2 className="w-16 h-16 text-[var(--cc-accent)] mx-auto" />
        <h1 className="font-heading font-extrabold text-4xl mt-6" data-testid="booking-success-title">Mission locked in.</h1>
        <p className="font-mono-cc text-[var(--cc-accent)] mt-3 text-xl" data-testid="booking-mission-number">{booked.mission.mission_number}</p>
        <p className="text-muted-foreground mt-3 text-lg leading-relaxed">
          {booked.mission.scheduled_date} at {booked.mission.scheduled_time} — estimate {fmtMoney(booked.estimate.total)}.
          {booked.mission.priority && <span className="text-purple-400 font-semibold"> Priority slot — you're first out.</span>}
        </p>
        {dep > 0 && (
          <div className="mt-8 rounded-2xl border border-[var(--cc-accent)]/40 bg-[var(--cc-accent)]/5 p-7 text-left" data-testid="book-deposit-card">
            <p className="font-heading font-bold text-lg flex items-center gap-2"><CreditCard className="w-5 h-5 text-[var(--cc-accent)]" /> Reserve your slot now</p>
            <p className="text-base text-muted-foreground mt-2 leading-relaxed">
              Lock in your crew with a {booked.estimate.deposit_percent}% deposit — {fmtMoney(dep)} today, {fmtMoney(booked.estimate.total - dep)} after service. Secure checkout via Stripe.
            </p>
            <Button data-testid="book-pay-deposit-button" onClick={payDeposit} disabled={depositLoading}
              className="w-full mt-5 rounded-full h-14 text-base btn-glow bg-[var(--cc-accent)] text-black hover:bg-[var(--cc-accent)]/90 font-bold">
              {depositLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Pay {fmtMoney(dep)} Deposit — Reserve Now</>}
            </Button>
          </div>
        )}
        <div className="mt-6 flex justify-center gap-4">
          <Link to="/portal/jobs"><Button variant={dep > 0 ? "outline" : "default"} data-testid="booking-success-portal-button" className={`rounded-full h-12 ${dep > 0 ? "" : "btn-glow bg-[var(--cc-accent)] text-black hover:bg-[var(--cc-accent)]/90 font-semibold"}`}>Track My Job</Button></Link>
          <Link to="/"><Button variant="ghost" className="rounded-full h-12">Back Home</Button></Link>
        </div>
      </div>
    );
  }

  const meta = STEP_META[step];
  const displayTotal = aiEst ? aiEst.total : estimate?.total;
  const canNext = {
    service: !!form.service_type,
    size: !!form.size,
    details: true,
    route: !!form.pickup.label.trim() && (form.same_place || !!form.dropoff.label.trim()),
    schedule: !!form.date,
    photos: true,
    summary: !!user && !loading && !aiLoading && displayTotal != null,
  }[step];

  const next = () => setStepIdx((i) => Math.min(STEPS.length - 1, i + 1));
  const back = () => setStepIdx((i) => Math.max(0, i - 1));
  const itemList = ITEM_CHIPS[form.service_type] || [];

  const summaryLines = estimate ? [
    { label: `${BOOK_SERVICES.find((s) => s.id === form.service_type)?.name || "Service"} — ${estimate.size_label}`, amt: estimate.scope_baseline || estimate.subtotal },
    (estimate.estimated_weight_lbs > 0 && estimate.weight_tier_price > estimate.scope_baseline) && {
      label: `Weight tier — ${estimate.weight_tier_label} · ${estimate.estimated_weight_lbs} lbs est.`,
      amt: estimate.weight_tier_price - estimate.scope_baseline,
    },
    estimate.same_place
      ? { label: "Same-place move — no transport fee", amt: 0 }
      : estimate.distance_fee > 0
        ? { label: `Distance: ${estimate.billable_miles} mi over ${estimate.included_miles} mi × $${estimate.per_mile_fee}`, amt: estimate.distance_fee }
        : { label: `Distance: ${estimate.distance_miles || 0} mi — within ${estimate.included_miles} mi included`, amt: 0 },
    estimate.stairs_fee > 0 && { label: `Stairs — ${estimate.stairs_flights} flight${estimate.stairs_flights !== 1 ? "s" : ""}`, amt: estimate.stairs_fee },
    estimate.electrical_fee > 0 && { label: "Electrical disconnect / reconnect", amt: estimate.electrical_fee },
    estimate.special_delivery_handling > 0 && { label: "Special-delivery handling", amt: estimate.special_delivery_handling },
    estimate.special_items_fee > 0 && { label: `Heavy / awkward items × ${estimate.special_items}`, amt: estimate.special_items_fee },
    estimate.stops_fee > 0 && { label: `Extra stops × ${estimate.extra_stops}`, amt: estimate.stops_fee },
    estimate.priority_fee > 0 && { label: "Last-minute priority", amt: estimate.priority_fee },
    estimate.discount && { label: `${estimate.discount.label} — ${estimate.discount.percent_off}% off`, amt: -estimate.discount.amount },
  ].filter(Boolean) : [];

  return (
    <div className="max-w-2xl mx-auto px-5 pt-12 lg:pt-16 pb-32 sm:pb-20" data-testid="booking-page">
      <input ref={globalFileRef} type="file" accept="image/*" multiple className="hidden" onChange={addPhotos} data-testid="book-global-photo-input" />

      <div className="flex items-center gap-1.5 mb-8">
        {STEPS.map((s, i) => (
          <div key={s} className={`h-1.5 flex-1 rounded-full transition-colors duration-300 ${i <= stepIdx ? "bg-[var(--cc-accent)]" : "bg-border"}`} />
        ))}
      </div>
      <p className="overline-cc mb-2">{meta.overline} — {String(stepIdx + 1).padStart(2, "0")} / {String(STEPS.length).padStart(2, "0")}</p>
      <h1 className="font-heading font-extrabold text-4xl sm:text-5xl tracking-tight mb-8">{meta.title}</h1>

      {step === "service" && (
        <div className="grid sm:grid-cols-2 gap-4 fade-up">
          {BOOK_SERVICES.map((s) => (
            <button key={s.id} data-testid={`book-service-${s.id}`}
              onClick={() => { set("service_type", s.id); setItems([]); setTimeout(next, 180); }}
              className={`text-left rounded-2xl border p-7 card-lift min-h-[140px] ${form.service_type === s.id ? "border-[var(--cc-accent)] bg-[var(--cc-accent)]/5" : "bg-card"}`}>
              <s.icon className="w-7 h-7 text-[var(--cc-accent)]" />
              <p className="font-heading font-bold text-xl mt-4">{s.name}</p>
              <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">{s.blurb}</p>
            </button>
          ))}
        </div>
      )}

      {step === "size" && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 fade-up">
          {BOOK_SIZES.map((s) => (
            <button key={s.id} data-testid={`book-size-${s.id}`}
              onClick={() => { set("size", s.id); setTimeout(next, 180); }}
              className={`text-left rounded-2xl border p-6 card-lift ${form.size === s.id ? "border-[var(--cc-accent)] bg-[var(--cc-accent)]/5" : "bg-card"}`}>
              <p className="font-heading font-bold text-lg">{s.name}</p>
              <p className="text-sm text-muted-foreground mt-1">{s.desc}</p>
            </button>
          ))}
        </div>
      )}

      {step === "details" && (
        <div className="space-y-8 fade-up">
          {itemList.length > 0 && (
            <div>
              <p className="font-heading font-bold text-xl mb-1">What's in the load?</p>
              <p className="text-sm text-muted-foreground mb-4">Tap to add — some items ask a quick follow-up (size, type). Tap the chip to edit specs.</p>
              <div className="grid grid-cols-2 gap-2.5">
                {itemList.map((name) => {
                  const cur = findItem(name);
                  const qty = cur?.qty || 0;
                  const catalogMeta = catalog?.items?.find((i) => i.name === name);
                  const specLabel = catalogMeta && cur?.spec
                    ? catalogMeta.options.find((o) => o.id === cur.spec)?.label
                    : null;
                  const tid = name.replace(/[^a-zA-Z0-9]+/g, "-");
                  if (qty === 0) return (
                    <button key={name} data-testid={`book-item-${tid}`}
                      onClick={() => {
                        if (catalogMeta) {
                          setDetailSheet({ open: true, name });
                          upsertItem(name, { qty: 1 });
                        } else incItem(name, 1);
                      }}
                      className={`${chip(false)} text-center text-sm`}>
                      {name}
                    </button>
                  );
                  return (
                    <div key={name} className={`${chip(true)} p-2.5 flex flex-col gap-1.5 text-sm`}>
                      <div className="flex items-center justify-between gap-2">
                        <button onClick={() => incItem(name, -1)} data-testid={`book-item-minus-${tid}`} className="w-8 h-8 rounded-full border border-[var(--cc-accent)]/40 flex items-center justify-center shrink-0"><Minus className="w-3.5 h-3.5" /></button>
                        <span className="truncate flex-1 text-center">{name}{qty > 1 ? ` ×${qty}` : ""}</span>
                        <button onClick={() => incItem(name, 1)} data-testid={`book-item-plus-${tid}`} className="w-8 h-8 rounded-full border border-[var(--cc-accent)]/40 flex items-center justify-center shrink-0"><Plus className="w-3.5 h-3.5" /></button>
                      </div>
                      {catalogMeta && (
                        <button onClick={() => setDetailSheet({ open: true, name })}
                          data-testid={`book-item-spec-${tid}`}
                          className="text-[11px] font-mono-cc uppercase tracking-wide text-[var(--cc-accent)]/80 flex items-center justify-center gap-1.5 hover:text-[var(--cc-accent)]">
                          <SlidersHorizontal className="w-3 h-3" />
                          {specLabel || "SET DETAILS"}
                          {(cur?.addons?.length || 0) > 0 && <span className="opacity-70">+{cur.addons.length}</span>}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <ItemDetailsSheet
            open={detailSheet.open}
            itemName={detailSheet.name}
            catalog={catalog}
            value={findItem(detailSheet.name)}
            onCancel={() => setDetailSheet({ open: false, name: "" })}
            onDone={(v) => { upsertItem(detailSheet.name, v); setDetailSheet({ open: false, name: "" }); }} />

          {items.some((it) => it.name === "Appliance") && (
            <div className="fade-up">
              <p className="font-heading font-bold text-xl mb-1">Electrical disconnect needed?</p>
              <p className="text-sm text-muted-foreground mb-4">Washer, dryer, fridge water line — we handle disconnect & reconnect.</p>
              <YesNo value={form.electrical} onChange={(v) => set("electrical", v)} testid="book-electrical" />
            </div>
          )}

          <div>
            <p className="font-heading font-bold text-xl mb-4">Stairs involved?</p>
            <YesNo value={form.stairs} onChange={(v) => set("stairs", v)} testid="book-stairs" />
            {form.stairs && (
              <div className="mt-4 fade-up">
                <p className="text-sm font-bold mb-2.5">How many flights?</p>
                <div className="grid grid-cols-3 gap-2.5">
                  {[1, 2, 3].map((f) => (
                    <button key={f} data-testid={`book-flights-${f}`} onClick={() => set("stairs_flights", f)}
                      className={`${chip(form.stairs_flights === f)} text-center`}>{f}{f === 3 ? "+" : ""}</button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div>
            <p className="font-heading font-bold text-xl mb-1">Heavy or awkward items?</p>
            <p className="text-sm text-muted-foreground mb-4">Piano, safe, treadmill — anything that takes extra muscle.</p>
            <div className="flex items-center gap-4">
              <button data-testid="book-special-minus" onClick={() => set("special_items", Math.max(0, form.special_items - 1))}
                className="w-12 h-12 rounded-full border flex items-center justify-center hover:border-[var(--cc-accent)] transition-colors duration-200"><Minus className="w-5 h-5" /></button>
              <span className="font-heading font-extrabold text-3xl w-10 text-center" data-testid="book-special-count">{form.special_items}</span>
              <button data-testid="book-special-plus" onClick={() => set("special_items", form.special_items + 1)}
                className="w-12 h-12 rounded-full border flex items-center justify-center hover:border-[var(--cc-accent)] transition-colors duration-200"><Plus className="w-5 h-5" /></button>
            </div>
          </div>
        </div>
      )}

      {step === "route" && (
        <div className="space-y-6 fade-up">
          <div className="space-y-2">
            <p className="text-sm font-bold">Pickup / service address</p>
            <AddressInput testid="book-pickup-input" value={form.pickup.label} placeholder="Start typing your address..."
              onChange={(t) => set("pickup", { label: t, lat: null, lng: null })}
              onSelect={(r) => set("pickup", r)} />
          </div>
          <button type="button" data-testid="book-same-place-toggle" onClick={() => set("same_place", !form.same_place)}
            className={`${chip(form.same_place)} w-full flex items-center justify-between`}>
            <span>Same-place move (on-site only, no transport)</span>
            <span className="font-mono-cc text-xs">{form.same_place ? "ON" : "OFF"}</span>
          </button>
          {!form.same_place && (
            <div className="space-y-2">
              <p className="text-sm font-bold">Dropoff address</p>
              <AddressInput testid="book-dropoff-input" value={form.dropoff.label} placeholder="310 Mission Ave, Oceanside"
                onChange={(t) => set("dropoff", { label: t, lat: null, lng: null })}
                onSelect={(r) => set("dropoff", r)} />
            </div>
          )}

          {routeLoading && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="book-route-loading">
              <Loader2 className="w-4 h-4 animate-spin" /> Calculating driving distance...
            </div>
          )}
          {route && !form.same_place && (
            <div className="fade-up space-y-3">
              <RouteMap route={route} className="h-44 sm:h-56" />
              <div className="flex flex-wrap items-center gap-3" data-testid="book-route-distance">
                <span className="inline-flex items-center gap-2 rounded-full border border-[var(--cc-accent)]/40 bg-[var(--cc-accent)]/10 px-4 py-2 font-mono-cc text-sm font-bold text-[var(--cc-accent)]">
                  <Route className="w-4 h-4" /> {route.distance_miles} MI · {route.duration_min} MIN
                </span>
                {estimate && (estimate.distance_fee > 0 ? (
                  <span className="text-sm text-muted-foreground" data-testid="book-distance-surcharge">
                    {estimate.billable_miles} mi over {estimate.included_miles} mi included × ${estimate.per_mile_fee} = <span className="font-bold text-foreground">{fmtMoney(estimate.distance_fee)}</span>
                  </span>
                ) : estimate.distance_miles > 0 ? (
                  <span className="text-sm text-emerald-500 font-semibold" data-testid="book-distance-included">Within the {estimate?.included_miles ?? 15} mi included radius — no surcharge</span>
                ) : null)}
              </div>
            </div>
          )}

          {!form.same_place && (
            <div>
              <p className="text-sm font-bold mb-2">Extra stops</p>
              {form.extra_stop_addresses.map((a, i) => (
                <div key={i} className="flex gap-2 mb-2">
                  <Input data-testid={`book-stop-input-${i}`} value={a} placeholder={`Stop ${String.fromCharCode(66 + i)} address`} className="h-12 rounded-xl"
                    onChange={(e) => { const arr = [...form.extra_stop_addresses]; arr[i] = e.target.value; set("extra_stop_addresses", arr); }} />
                  <Button size="icon" variant="outline" className="shrink-0 rounded-full h-12 w-12" data-testid={`book-stop-remove-${i}`}
                    onClick={() => set("extra_stop_addresses", form.extra_stop_addresses.filter((_, j) => j !== i))}>
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ))}
              {form.extra_stop_addresses.length < 5 && (
                <Button variant="outline" className="rounded-full h-11" data-testid="book-add-stop-button"
                  onClick={() => set("extra_stop_addresses", [...form.extra_stop_addresses, ""])}>
                  <Plus className="w-4 h-4 mr-1" /> Add stop
                </Button>
              )}
            </div>
          )}
        </div>
      )}

      {step === "schedule" && (
        <div className="space-y-7 fade-up">
          <button data-testid="book-date-field" onClick={() => setDateOpen(true)}
            className="w-full flex items-center justify-between rounded-2xl border bg-card px-6 py-6 hover:border-[var(--cc-accent)]/60 transition-colors duration-200">
            <span className="flex items-center gap-3 font-bold text-lg"><CalendarDays className="w-6 h-6 text-[var(--cc-accent)]" /> Mission date</span>
            <span className={`font-heading text-lg ${form.date ? "font-bold" : "text-muted-foreground"}`}>{form.date ? prettyDate(form.date) : "Select date"}</span>
          </button>
          <DateSheet open={dateOpen} value={form.date} onCancel={() => setDateOpen(false)} onDone={(v) => { set("date", v); setDateOpen(false); }} />

          <button data-testid="book-time-field" onClick={() => setTimeOpen(true)}
            className="w-full flex items-center justify-between rounded-2xl border bg-card px-6 py-6 hover:border-[var(--cc-accent)]/60 transition-colors duration-200">
            <span className="flex items-center gap-3 font-bold text-lg"><Clock className="w-6 h-6 text-[var(--cc-accent)]" /> Arrival time</span>
            <span className="font-heading text-lg font-bold">{fmtTime(form.time)}</span>
          </button>
          <TimeWheelSheet open={timeOpen} value={form.time} title="Arrival Time"
            onCancel={() => setTimeOpen(false)}
            onDone={(v) => { set("time", v); setTimeOpen(false); }} />

          <button type="button" data-testid="book-priority-toggle" onClick={() => set("priority", !form.priority)}
            className={`w-full text-left rounded-2xl border p-6 transition-colors duration-200 flex items-center justify-between gap-3 ${form.priority ? "border-purple-500 bg-purple-500/10" : "bg-card hover:border-purple-500/50"}`}>
            <div className="flex items-center gap-4">
              <Zap className={`w-6 h-6 shrink-0 ${form.priority ? "text-purple-400" : "text-muted-foreground"}`} />
              <div>
                <p className="font-heading font-bold text-lg">Last-minute priority</p>
                <p className="text-sm text-muted-foreground mt-0.5">Jump the queue — first crew out, guaranteed slot</p>
              </div>
            </div>
            <span className={`font-mono-cc text-lg font-bold shrink-0 ${form.priority ? "text-purple-400" : "text-muted-foreground"}`}>+{fmtMoney(estimate?.priority ? estimate.priority_fee : 75)}</span>
          </button>
        </div>
      )}

      {step === "photos" && (
        <div className="space-y-7 fade-up">
          <div className="rounded-2xl border border-dashed border-[var(--cc-accent)]/50 bg-[var(--cc-accent)]/[0.03] p-7" data-testid="book-photos-section">
            <div className="flex items-start gap-4">
              <ScanEye className="w-7 h-7 text-[var(--cc-accent)] shrink-0 mt-1" />
              <div className="flex-1 min-w-0">
                <p className="font-heading font-bold text-lg">Photo estimate</p>
                <p className="text-sm text-muted-foreground mt-1 leading-relaxed">Add up to 6 photos — we size up volume, weight, and access for one precise number.</p>
                <div className="grid grid-cols-2 gap-3 mt-5">
                  <label data-testid="book-photo-camera" className="flex items-center justify-center gap-2 rounded-2xl border-2 border-dashed py-5 cursor-pointer hover:border-[var(--cc-accent)] transition-colors duration-200 font-semibold text-sm">
                    <Camera className="w-5 h-5 text-[var(--cc-accent)]" /> Take photo
                    <input type="file" accept="image/*" capture="environment" className="hidden" onChange={addPhotos} />
                  </label>
                  <label data-testid="book-photo-add" className="flex items-center justify-center gap-2 rounded-2xl border-2 border-dashed py-5 cursor-pointer hover:border-[var(--cc-accent)] transition-colors duration-200 font-semibold text-sm">
                    <Upload className="w-5 h-5 text-[var(--cc-accent)]" /> Upload
                    <input type="file" accept="image/*" multiple className="hidden" onChange={addPhotos} data-testid="book-photo-input" />
                  </label>
                </div>
                {photos.length > 0 && (
                  <div className="flex flex-wrap gap-2.5 mt-4">
                    {photos.map((p, i) => (
                      <div key={i} className="relative w-20 h-20 rounded-xl overflow-hidden border" data-testid={`book-photo-${i}`}>
                        <img src={p} alt={`Job ${i + 1}`} className="w-full h-full object-cover" />
                        <button type="button" data-testid={`book-photo-remove-${i}`} onClick={() => { setPhotos((x) => x.filter((_, j) => j !== i)); setAiEst(null); }}
                          className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/70 text-white flex items-center justify-center"><X className="w-3 h-3" /></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div>
            <p className="font-heading font-bold text-xl mb-3">Promo / referral code</p>
            {form.promo_code ? (
              <div className="flex items-center gap-2">
                <span data-testid="book-promo-applied" className="inline-flex items-center gap-2 rounded-full border border-emerald-500/50 bg-emerald-500/10 text-emerald-500 px-5 py-2.5 text-base font-semibold">
                  <Tag className="w-4 h-4" /> {form.promo_code} — {promoInfo?.percent_off}% off
                </span>
                <Button size="icon" variant="ghost" className="rounded-full" data-testid="book-promo-remove"
                  onClick={() => { set("promo_code", ""); setPromoInfo(null); setPromoInput(""); }}><X className="w-4 h-4" /></Button>
              </div>
            ) : (
              <div className="flex gap-2 max-w-sm">
                <Input data-testid="book-promo-input" value={promoInput} onChange={(e) => setPromoInput(e.target.value)} placeholder="CC-XXXXXX" className="uppercase h-12 rounded-xl" />
                <Button variant="outline" className="rounded-full shrink-0 h-12" data-testid="book-promo-apply" onClick={applyCode}>Apply</Button>
              </div>
            )}
          </div>

          <div>
            <p className="font-heading font-bold text-xl mb-3">Anything we should know?</p>
            <Textarea data-testid="book-details-input" rows={3} value={form.details} onChange={(e) => set("details", e.target.value)}
              placeholder="Fragile pieces, elevators, parking notes..." className="text-base rounded-xl" />
          </div>
        </div>
      )}

      {step === "summary" && (
        <div className="space-y-6 fade-up">
          <div className="rounded-2xl border bg-card p-7 corner-cc" data-testid="book-summary-card">
            {aiLoading ? (
              <div className="py-10 text-center" data-testid="book-ai-analyzing">
                <ScanEye className="w-12 h-12 text-[var(--cc-accent)] mx-auto animate-pulse" />
                <p className="font-heading font-bold text-lg mt-4">Reviewing your {photos.length} photo{photos.length !== 1 ? "s" : ""}…</p>
                <p className="text-sm text-muted-foreground mt-1.5">Sizing up volume, weight, and access to lock your estimate.</p>
                <Loader2 className="w-5 h-5 animate-spin mx-auto mt-4 text-muted-foreground" />
              </div>
            ) : displayTotal != null ? (
              <>
                <p className="overline-cc mb-5">Itemized Estimate</p>
                <div className="space-y-3">
                  {summaryLines.map((l, i) => (
                    <div key={i} className="flex items-start justify-between gap-4 text-base" data-testid={`book-summary-line-${i}`}>
                      <span className={l.amt < 0 ? "text-emerald-500 font-semibold" : "text-muted-foreground"}>{l.label}</span>
                      <span className={`font-mono-cc font-semibold shrink-0 ${l.amt < 0 ? "text-emerald-500" : l.amt === 0 ? "text-muted-foreground" : ""}`}>
                        {l.amt === 0 ? "—" : (l.amt < 0 ? "−" : "") + fmtMoney(Math.abs(l.amt))}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="border-t mt-5 pt-5 flex items-end justify-between">
                  <p className="font-heading font-bold text-lg">Mission total</p>
                  <p className="font-heading font-extrabold text-5xl tracking-tight" data-testid="book-estimate-total">{fmtMoney(displayTotal)}</p>
                </div>
                {aiEst && (
                  <div className="mt-4 rounded-xl border border-[var(--cc-accent)]/30 bg-[var(--cc-accent)]/5 p-4" data-testid="book-ai-estimate-note">
                    <p className="text-xs font-semibold text-[var(--cc-accent)] flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5" /> ESTIMATE — BASED ON YOUR {photos.length} PHOTO{photos.length !== 1 ? "S" : ""}</p>
                    {aiEst.note && <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">{aiEst.note}</p>}
                  </div>
                )}
                <p className="mt-5 flex items-center gap-2 text-sm text-muted-foreground"><ShieldCheck className="w-5 h-5 text-[var(--cc-accent)] shrink-0" /> Crew, truck, equipment, protection, and $1M insurance included.</p>
                {(aiEst ? aiEst.deposit_amount : estimate?.deposit_amount) > 0 && (
                  <p className="mt-2 flex items-center gap-2 text-sm text-muted-foreground" data-testid="book-deposit-note">
                    <CreditCard className="w-5 h-5 text-[var(--cc-accent)] shrink-0" /> Reserve with a {estimate?.deposit_percent ?? 20}% deposit ({fmtMoney(aiEst ? aiEst.deposit_amount : estimate.deposit_amount)}) — pay the rest after service.
                  </p>
                )}
              </>
            ) : <Loader2 className="w-5 h-5 animate-spin" />}
          </div>

          {route && !form.same_place && <RouteMap route={route} className="h-40 sm:h-52" />}

          <div className="rounded-2xl border bg-secondary/40 p-6 text-base space-y-1.5">
            <p className="flex items-start gap-2"><CalendarDays className="w-4 h-4 mt-1 text-[var(--cc-accent)] shrink-0" /> {form.date ? prettyDate(form.date) : "—"} · arrival {fmtTime(form.time)}{form.priority ? " · PRIORITY" : ""}</p>
            <p className="flex items-start gap-2"><MapPin className="w-4 h-4 mt-1 text-[var(--cc-accent)] shrink-0" /> {form.pickup.label}{!form.same_place && form.dropoff.label ? ` → ${form.dropoff.label}` : ""}</p>
            {items.length > 0 && (
              <p className="text-sm text-muted-foreground pl-6">
                {items.map((it) => {
                  const meta = catalog?.items?.find((i) => i.name === it.name);
                  const s = meta?.options.find((o) => o.id === it.spec)?.label;
                  return `${it.name}${it.qty > 1 ? ` ×${it.qty}` : ""}${s ? ` (${s})` : ""}`;
                }).join(", ")}
              </p>
            )}
          </div>

          {!user && (
            <div className="rounded-2xl border border-[var(--cc-accent)]/40 bg-card p-7 text-center" data-testid="book-auth-required">
              <p className="font-heading font-bold text-lg">Sign in to confirm your booking</p>
              <p className="text-base text-muted-foreground mt-1">Your booking is tracked in your customer portal.</p>
              <div className="mt-5 flex justify-center gap-3">
                <Link to="/login"><Button data-testid="book-login-button" className="rounded-full h-12 px-6 btn-glow bg-[var(--cc-accent)] text-black hover:bg-[var(--cc-accent)]/90 font-semibold">Sign In</Button></Link>
                <Link to="/register"><Button variant="outline" className="rounded-full h-12 px-6">Create Account</Button></Link>
              </div>
            </div>
          )}
        </div>
      )}

      {step !== "service" && (
        <div className="fixed bottom-0 inset-x-0 z-40 border-t glass-cc bg-background/85 px-5 py-3 sm:static sm:border-0 sm:bg-transparent sm:px-0 sm:py-0 sm:mt-10" data-testid="book-cta-bar">
          <div className="max-w-2xl mx-auto flex items-center gap-3">
            <Button variant="ghost" size="icon" className="rounded-full h-14 w-14 shrink-0" onClick={back} data-testid="book-back-button" aria-label="Back">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            {step !== "summary" && step !== "photos" && (
              <button type="button" onClick={() => globalFileRef.current?.click()} data-testid="book-camera-chip" aria-label="Attach photos"
                className="h-14 w-14 shrink-0 rounded-full border flex items-center justify-center hover:border-[var(--cc-accent)] transition-colors duration-200 relative">
                <Camera className="w-5 h-5 text-[var(--cc-accent)]" />
                {photos.length > 0 && <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-[var(--cc-accent)] text-black text-[10px] font-bold flex items-center justify-center">{photos.length}</span>}
              </button>
            )}
            {estimate && stepIdx >= 1 && (
              <div className="hidden sm:block mr-1" data-testid="book-live-estimate">
                <p className="text-[10px] font-mono-cc uppercase tracking-widest text-muted-foreground">Est.</p>
                <p className="font-heading font-extrabold text-2xl leading-none">{fmtMoney(displayTotal ?? estimate.total)}</p>
              </div>
            )}
            {step === "summary" ? (
              user ? (
                <Button data-testid="book-confirm-button" disabled={!canNext}
                  className="flex-1 rounded-full h-14 text-base btn-glow bg-[var(--cc-accent)] text-black hover:bg-[var(--cc-accent)]/90 font-bold" onClick={confirm}>
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Confirm Mission — {displayTotal != null ? fmtMoney(displayTotal) : "..."} <ArrowRight className="w-5 h-5 ml-1.5" /></>}
                </Button>
              ) : (
                <Link to="/login" className="flex-1"><Button className="w-full rounded-full h-14 text-base btn-glow bg-[var(--cc-accent)] text-black hover:bg-[var(--cc-accent)]/90 font-bold">Sign In to Confirm</Button></Link>
              )
            ) : (
              <Button data-testid="book-next-button" disabled={!canNext}
                className="flex-1 rounded-full h-14 text-base btn-glow bg-[var(--cc-accent)] text-black hover:bg-[var(--cc-accent)]/90 font-bold" onClick={next}>
                {estimate && stepIdx >= 1 ? <span className="sm:hidden mr-2 font-mono-cc text-sm opacity-80">{fmtMoney(displayTotal ?? estimate.total)} ·</span> : null}
                {step === "photos" ? "Review Mission" : "Continue"} <ArrowRight className="w-5 h-5 ml-1.5" />
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
